// ============================================================================
// TENANT APPLICATION ASSIGNMENT MASTER SCREEN
// ============================================================================
// Cross-tenant view of all tenant ↔ application assignments.
// Drives the "Application Assignments" sidebar item under Tenant Management.
// Follows the mandatory blueprint in tenant-hub-web/CLAUDE.md.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader,
} from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from '../../../../services/notificationService';
import {
  useTenantAppAssignments,
  useRemoveTenantAppAssignment,
  useSuspendTenantAppAssignment,
  useResumeTenantAppAssignment,
} from 'hooks/api';
import type {
  TenantAppAssignment,
  TenantAppAssignmentSearchRequest,
  AssignmentStatus,
} from 'api/types';
import {
  SUBSCRIPTION_PLANS,
  BILLING_CYCLES,
  HEALTH_STATUSES,
  ASSIGNMENT_STATUSES,
} from 'api/types/tenantAppAssignments.types';
import TenantApplicationAssignmentFormModal from '../../../modals/modules/tenant-management/TenantApplicationAssignmentFormModal';
import TenantApplicationAssignmentViewModal from '../../../modals/modules/tenant-management/TenantApplicationAssignmentViewModal';

// ============================================================================
// TYPES
// ============================================================================

type ViewMode = 'list' | 'grid';
// AssignmentRow narrows TenantAppAssignment.id (ID = string | number) to string
// so SyutGrid's row-key handling stays consistent.
type AssignmentRow = Omit<TenantAppAssignment, 'id'> & { id: string };

interface ScreenProps {
  /** Optional pre-applied tenant filter — set when arriving from a tenant deep-link. */
  preFilteredTenantId?: string;
  /** Optional pre-applied application filter — set when arriving from an app deep-link. */
  preFilteredApplicationId?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TenantApplicationAssignmentMasterScreen: React.FC<ScreenProps> = ({
  preFilteredTenantId,
  preFilteredApplicationId,
}) => {
  // Read URL query params on mount as a fallback (when not passed as props by ComponentRegistry)
  const initialTenantId = useMemo(() => {
    if (preFilteredTenantId) return preFilteredTenantId;
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    return params.get('tenantId') || undefined;
  }, [preFilteredTenantId]);

  const initialApplicationId = useMemo(() => {
    if (preFilteredApplicationId) return preFilteredApplicationId;
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    return params.get('applicationId') || undefined;
  }, [preFilteredApplicationId]);

  // ── Server-side query state ──────────────────────────────────────────────
  const [queryParams, setQueryParams] = useState<TenantAppAssignmentSearchRequest>(() => ({
    page: 1,
    pageSize: 25,
    includeSuspended: true,
    includeRemoved: false,
    sortBy: 'installedDate',
    sortOrder: 'desc',
    tenantIds: initialTenantId ? [initialTenantId] : undefined,
    applicationIds: initialApplicationId ? [initialApplicationId] : undefined,
  }));

  const { data, loading, pagination, refetch } = useTenantAppAssignments(queryParams);

  const { remove, loading: removing } = useRemoveTenantAppAssignment();
  const { suspend, loading: suspending } = useSuspendTenantAppAssignment();
  const { resume, loading: resuming } = useResumeTenantAppAssignment();

  // ── View / search / filter state ─────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [planFilter, setPlanFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<AssignmentStatus | ''>('');
  const [billingCycleFilter, setBillingCycleFilter] = useState<string>('');
  const [healthFilter, setHealthFilter] = useState<string>('');
  const [trialEndFromFilter, setTrialEndFromFilter] = useState<string>('');
  const [trialEndToFilter, setTrialEndToFilter] = useState<string>('');
  const [subEndFromFilter, setSubEndFromFilter] = useState<string>('');
  const [subEndToFilter, setSubEndToFilter] = useState<string>('');
  const [installedFromFilter, setInstalledFromFilter] = useState<string>('');
  const [installedToFilter, setInstalledToFilter] = useState<string>('');
  const [showSuspended, setShowSuspended] = useState(true);
  const [showRemoved, setShowRemoved] = useState(false);

  // ── Modal state ──────────────────────────────────────────────────────────
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<TenantAppAssignment | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Suspend / Resume confirmation
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [resumeConfirmOpen, setResumeConfirmOpen] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionReasonError, setSuspensionReasonError] = useState('');

  // ── Search debounce → pushes to queryParams.searchTerm ──────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Pagination handlers ──────────────────────────────────────────────────
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // ── Apply filters ────────────────────────────────────────────────────────
  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      planNames: planFilter ? [planFilter] : undefined,
      statuses: statusFilter ? [statusFilter] : undefined,
      billingCycles: billingCycleFilter ? [billingCycleFilter] : undefined,
      healthStatuses: healthFilter ? [healthFilter] : undefined,
      trialEndingFrom: trialEndFromFilter || undefined,
      trialEndingTo: trialEndToFilter || undefined,
      subscriptionEndingFrom: subEndFromFilter || undefined,
      subscriptionEndingTo: subEndToFilter || undefined,
      installedFrom: installedFromFilter || undefined,
      installedTo: installedToFilter || undefined,
      includeSuspended: showSuspended,
      includeRemoved: showRemoved,
    }));
    setFilterOpen(false);
  }, [
    planFilter, statusFilter, billingCycleFilter, healthFilter,
    trialEndFromFilter, trialEndToFilter, subEndFromFilter, subEndToFilter,
    installedFromFilter, installedToFilter, showSuspended, showRemoved,
  ]);

  const clearFilters = useCallback(() => {
    setPlanFilter('');
    setStatusFilter('');
    setBillingCycleFilter('');
    setHealthFilter('');
    setTrialEndFromFilter('');
    setTrialEndToFilter('');
    setSubEndFromFilter('');
    setSubEndToFilter('');
    setInstalledFromFilter('');
    setInstalledToFilter('');
    setShowSuspended(true);
    setShowRemoved(false);
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      planNames: undefined,
      statuses: undefined,
      billingCycles: undefined,
      healthStatuses: undefined,
      trialEndingFrom: undefined,
      trialEndingTo: undefined,
      subscriptionEndingFrom: undefined,
      subscriptionEndingTo: undefined,
      installedFrom: undefined,
      installedTo: undefined,
      includeSuspended: true,
      includeRemoved: false,
    }));
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((row: AssignmentRow) => {
    setFormMode('edit');
    setSelectedRecord(row);
    setFormModalOpen(true);
  }, []);

  const handleView = useCallback((row: AssignmentRow) => {
    setSelectedRecord(row);
    setViewModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((row: AssignmentRow) => {
    setSelectedRecord(row);
    setDeleteConfirmOpen(true);
  }, []);

  const handleSuspendClick = useCallback((row: AssignmentRow) => {
    setSelectedRecord(row);
    setSuspensionReason('');
    setSuspensionReasonError('');
    setSuspendConfirmOpen(true);
  }, []);

  const handleResumeClick = useCallback((row: AssignmentRow) => {
    setSelectedRecord(row);
    setResumeConfirmOpen(true);
  }, []);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await remove(selectedRecord.tenantId, selectedRecord.appId);
      setDeleteConfirmOpen(false);
      notificationService.toast.success(
        `"${selectedRecord.appName}" removed from ${selectedRecord.tenantName}.`
      );
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while removing the assignment.');
    }
  }, [selectedRecord, remove, refetch]);

  const handleConfirmSuspend = useCallback(async () => {
    if (!selectedRecord) return;
    if (!suspensionReason.trim()) {
      setSuspensionReasonError('Reason is required');
      return;
    }
    try {
      await suspend(selectedRecord.tenantId, selectedRecord.appId, suspensionReason.trim());
      setSuspendConfirmOpen(false);
      notificationService.toast.success(
        `Suspended "${selectedRecord.appName}" for ${selectedRecord.tenantName}.`
      );
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while suspending the assignment.');
    }
  }, [selectedRecord, suspensionReason, suspend, refetch]);

  const handleConfirmResume = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await resume(selectedRecord.tenantId, selectedRecord.appId);
      setResumeConfirmOpen(false);
      notificationService.toast.success(
        `Resumed "${selectedRecord.appName}" for ${selectedRecord.tenantName}.`
      );
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while resuming the assignment.');
    }
  }, [selectedRecord, resume, refetch]);

  // ── Grid column definitions ──────────────────────────────────────────────
  const tableColumns: SyutGridColumn<AssignmentRow>[] = useMemo(() => [
    {
      id: 'tenant',
      header: 'Tenant',
      accessorKey: 'tenantName',
      // `any` on row here matches the pattern used elsewhere (TenantMasterScreen) —
      // SyutGridColumn's contextual typing doesn't propagate into cell callbacks
      // through the array generic, so explicit annotation is the cleanest path.
      cell: (props: any) => {
        const r: AssignmentRow = props.row.original;
        return (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontWeight: 600 }}>{r.tenantName}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.tenantCode}</div>
          </div>
        );
      },
      size: 200,
      enableSorting: true,
    },
    {
      id: 'application',
      header: 'Application',
      accessorKey: 'appName',
      cell: (props: any) => {
        const r: AssignmentRow = props.row.original;
        return (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontWeight: 600 }}>{r.appName}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{r.appCode}</div>
          </div>
        );
      },
      size: 200,
      enableSorting: true,
    },
    {
      id: 'plan',
      header: 'Plan',
      accessorKey: 'subscriptionPlanName',
      cell: (props: any) => props.row.original.subscriptionPlanName || '—',
      size: 130,
      enableSorting: true,
    },
    {
      id: 'billingCycle',
      header: 'Billing',
      accessorKey: 'billingCycle',
      cell: (props: any) => props.row.original.billingCycle || '—',
      size: 100,
      enableSorting: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'derivedStatus',
      cell: (props: any) => { const row = props.row;
        const s = row.original.derivedStatus;
        const cls =
          s === 'Active' ? 'badge-active' :
          s === 'Trial' ? 'badge-warning' :
          s === 'Suspended' ? 'badge-suspended' :
          'badge-inactive';
        return <span className={`badge ${cls}`}>{s}</span>;
      },
      size: 110,
      enableSorting: true,
    },
    {
      id: 'maxUsers',
      header: 'Max Users',
      accessorKey: 'maxUsers',
      size: 100,
      enableSorting: true,
    },
    {
      id: 'trialEnd',
      header: 'Trial Ends',
      accessorKey: 'trialEndDate',
      cell: (props: any) => props.row.original.trialEndDate
        ? new Date(props.row.original.trialEndDate).toLocaleDateString()
        : '—',
      size: 120,
      enableSorting: true,
    },
    {
      id: 'subEnd',
      header: 'Subscription End',
      accessorKey: 'subscriptionEndDate',
      cell: (props: any) => props.row.original.subscriptionEndDate
        ? new Date(props.row.original.subscriptionEndDate).toLocaleDateString()
        : '—',
      size: 140,
      enableSorting: true,
    },
    {
      id: 'installedDate',
      header: 'Installed',
      accessorKey: 'installedDate',
      cell: (props: any) => props.row.original.installedDate
        ? new Date(props.row.original.installedDate).toLocaleDateString()
        : '—',
      size: 110,
      enableSorting: true,
    },
  ], []);

  // ── Row actions ──────────────────────────────────────────────────────────
  const getRowActions = useCallback((row: AssignmentRow): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View', icon: <IconSystem.eye size={16} />, onClick: () => handleView(row) },
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(row) },
    ];
    if (row.isSuspended) {
      actions.push({ id: 'resume', label: 'Resume', icon: <IconSystem.refresh size={16} />, onClick: () => handleResumeClick(row) });
    } else {
      actions.push({ id: 'suspend', label: 'Suspend', icon: <IconSystem.lock size={16} />, onClick: () => handleSuspendClick(row) });
    }
    actions.push({
      id: 'remove',
      label: 'Remove',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(row),
      variant: 'danger',
    });
    return actions;
  }, [handleView, handleEdit, handleSuspendClick, handleResumeClick, handleDeleteClick]);

  // ── Toolbar action buttons (icon-only — matches TenantMasterScreen pattern) ──
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Assign Application',
      onClick: handleAddNew,
      tooltip: 'Assign Application to Tenant',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => { refetch(); },
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ], [handleAddNew, refetch]);

  // ── Map data → AssignmentRow (id must be string for SyutGrid row keys) ──
  const rows: AssignmentRow[] = useMemo(
    () => data.map((d): AssignmentRow => ({ ...d, id: String(d.id) })),
    [data]
  );

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Application Assignments"
        description="Assign applications to tenants and manage their subscriptions, Keycloak clients, and access limits."
        icon={<IconSystem.link size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search by tenant or application…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
          ],
          onChange: (m) => setViewMode(m as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={rows}
              columns={tableColumns}
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No application assignments found"
            />
          )}

          {viewMode === 'grid' && (
            <div style={{ padding: '1rem', overflow: 'auto', height: '100%' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Grid view coming soon. Use list view for now.
              </p>
            </div>
          )}
        </div>

        {/* Filter panel — conditionally rendered (matches TenantMasterScreen pattern). */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Plan"
                options={[{ value: '', label: 'All Plans' }, ...SUBSCRIPTION_PLANS.map((p) => ({ value: p, label: p }))]}
                value={planFilter}
                onChange={(v) => setPlanFilter((v as string) || '')}
                placeholder="Select plan..."
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[{ value: '', label: 'All Statuses' }, ...ASSIGNMENT_STATUSES.map((s) => ({ value: s, label: s }))]}
                value={statusFilter}
                onChange={(v) => setStatusFilter(((v as string) || '') as AssignmentStatus | '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Billing Cycle"
                options={[{ value: '', label: 'All Cycles' }, ...BILLING_CYCLES.map((c) => ({ value: c, label: c }))]}
                value={billingCycleFilter}
                onChange={(v) => setBillingCycleFilter((v as string) || '')}
                placeholder="Select cycle..."
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Health"
                options={[{ value: '', label: 'All Health States' }, ...HEALTH_STATUSES.map((h) => ({ value: h, label: h }))]}
                value={healthFilter}
                onChange={(v) => setHealthFilter((v as string) || '')}
                placeholder="Select health..."
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Trial Ending From</label>
              <input type="date" className="form-input" value={trialEndFromFilter}
                onChange={(e) => setTrialEndFromFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Trial Ending To</label>
              <input type="date" className="form-input" value={trialEndToFilter}
                onChange={(e) => setTrialEndToFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Subscription Ending From</label>
              <input type="date" className="form-input" value={subEndFromFilter}
                onChange={(e) => setSubEndFromFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Subscription Ending To</label>
              <input type="date" className="form-input" value={subEndToFilter}
                onChange={(e) => setSubEndToFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Installed From</label>
              <input type="date" className="form-input" value={installedFromFilter}
                onChange={(e) => setInstalledFromFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Installed To</label>
              <input type="date" className="form-input" value={installedToFilter}
                onChange={(e) => setInstalledToFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <input type="checkbox" checked={showSuspended} onChange={(e) => setShowSuspended(e.target.checked)} />
                Include suspended
              </label>
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem' }}>
                <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
                Include removed
              </label>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* ── Form Modal (assign / edit) ──────────────────────────────────── */}
      <TenantApplicationAssignmentFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onClose={() => setFormModalOpen(false)}
        onSaved={handleSaveSuccess}
      />

      {/* ── View Modal (read-only) ──────────────────────────────────────── */}
      <TenantApplicationAssignmentViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
      />

      {/* ── Delete confirmation ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Remove Assignment"
        message={selectedRecord
          ? `Permanently remove "${selectedRecord.appName}" from ${selectedRecord.tenantName}? Their data will not be deleted but they will lose access immediately.`
          : ''}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removing}
        loadingMessage="Removing..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      {/* ── Suspend confirmation (with reason capture) ──────────────────── */}
      {suspendConfirmOpen && selectedRecord && (
        <>
          <div className="modal-backdrop" onClick={() => !suspending && setSuspendConfirmOpen(false)} />
          <div className="modal-container modal-sm">
            {suspending && (
              <div style={{
                position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                borderRadius: 'inherit',
              }}>
                <div style={{
                  width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                  borderTopColor: '#ef4444', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                }} />
              </div>
            )}
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Suspend Assignment</h2>
              <button className="modal-close" onClick={() => setSuspendConfirmOpen(false)} disabled={suspending}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
                Suspend <strong>{selectedRecord.tenantName}</strong>'s access to <strong>{selectedRecord.appName}</strong>?
                Users will be unable to log in. You can resume at any time.
              </p>
              <div className="form-group">
                <label className="form-label">Reason <span className="text-danger">*</span></label>
                <textarea
                  className={`form-textarea${suspensionReasonError ? ' form-field-error' : ''}`}
                  rows={3}
                  value={suspensionReason}
                  onChange={(e) => {
                    setSuspensionReason(e.target.value);
                    if (suspensionReasonError) setSuspensionReasonError('');
                  }}
                  placeholder="e.g. Non-payment, security incident, customer request"
                />
                {suspensionReasonError && <div className="form-error-message">{suspensionReasonError}</div>}
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setSuspendConfirmOpen(false)} disabled={suspending}>Cancel</button>
              <button className="btn btn-danger" onClick={handleConfirmSuspend} disabled={suspending}>Suspend</button>
            </div>
          </div>
        </>
      )}

      {/* ── Resume confirmation ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={resumeConfirmOpen}
        title="Resume Assignment"
        message={selectedRecord
          ? `Resume ${selectedRecord.tenantName}'s access to ${selectedRecord.appName}?`
          : ''}
        confirmLabel="Resume"
        cancelLabel="Cancel"
        variant="default"
        loading={resuming}
        loadingMessage="Resuming..."
        onConfirm={handleConfirmResume}
        onCancel={() => setResumeConfirmOpen(false)}
      />
    </div>
  );
};

export default TenantApplicationAssignmentMasterScreen;
